import { ParcelLocationType } from "@/types/dashboard";
import L from "leaflet";
import "leaflet-draw";
import { useEffect, useRef } from "react";

export const useDisplayParcels = (
  map: L.Map | null,
  data: {
    id: number | string;
    name: string;
    parcels: ParcelLocationType[];
    locations: ParcelLocationType[];
  }[],
  onClick?: (
    parcel: ParcelLocationType,
    polygon: L.Polygon,
    isCurrentPolygon: boolean
  ) => void,
  currentParcel,
  isReadOnly: boolean = false
) => {
  const editingPolygonRef = useRef<L.Polygon | null>(null);
  const polygonMapRef = useRef<Map<number, L.Polygon>>(new Map());
  const parcelMapRef = useRef<Map<number, L.Polygon>>(new Map());
  const markerMapRef = useRef<Map<number, L.Marker>>(new Map());
  const toggledPolygonsRef = useRef<Set<number>>(new Set());
  const currentParcelRef = useRef(currentParcel);

  useEffect(() => {
    currentParcelRef.current = currentParcel;
  }, [currentParcel]);

  const customIcon = L.icon({
    iconUrl: "/pinco.png",
    iconSize: [28, 28],
    iconAnchor: [5, 12],
    popupAnchor: [0, -32],
  });

  const updatePolygonInstance = (parcel: ParcelLocationType) => {
    const polygon =
      polygonMapRef.current.get(parcel.id) ||
      parcelMapRef.current.get(parcel.id);
    if (!polygon) return;

    const newLatLngs = parcel.coordinates.map(([lat, lng]) =>
      L.latLng(lat, lng)
    );
    polygon.setLatLngs(newLatLngs);
    polygon.setStyle({
      color: parcel.strokeColor,
      weight: parcel.strokeWidth,
      fillColor: parcel.fillColor,
      opacity: 1,
      fillOpacity: parcel.opacity ?? 0.4,
    });

    const wasEditing = polygon.editing?.enabled?.();
    polygon.editing?.disable();
    if (wasEditing) polygon.editing?.enable();
  };

  useEffect(() => {
    if (!map || !data || !data.length) return;

    if (!map.getPane("defaultPolygonPane")) {
      map.createPane("defaultPolygonPane");
      map.getPane("defaultPolygonPane")!.style.zIndex = "410";
    }
    if (!map.getPane("highlightedPolygonPane")) {
      map.createPane("highlightedPolygonPane");
      map.getPane("highlightedPolygonPane")!.style.zIndex = "420";
    }

    if (!map.getPane("defaultPolygonPane")) {
      map.createPane("defaultPolygonPane");
      map.getPane("defaultPolygonPane")!.style.zIndex = "410";
    }
    if (!map.getPane("highlightedPolygonPane")) {
      map.createPane("highlightedPolygonPane");
      map.getPane("highlightedPolygonPane")!.style.zIndex = "420";
    }

    const layerGroup = L.layerGroup().addTo(map);
    const displayThreshold = 12;

    const updateVisibility = () => {
      const zoom = map.getZoom();
      const allParcels = data.flatMap((f) => f.parcels || []);
      const allLocations = data.flatMap((f) => f.locations || []);

      toggledPolygonsRef.current.clear();
      [...allParcels, ...allLocations].forEach((parcel) => {
        const polygon =
          polygonMapRef.current.get(parcel.id) ||
          parcelMapRef.current.get(parcel.id);
        const marker = markerMapRef.current.get(parcel.id);

        if (zoom >= displayThreshold) {
          if (marker) layerGroup.removeLayer(marker);
          if (polygon && !layerGroup.hasLayer(polygon)) {
            layerGroup.addLayer(polygon);
          }
        } else {
          if (polygon) layerGroup.removeLayer(polygon);

          if (!marker && polygon) {
            const center = polygon.getBounds().getCenter();
            const markerInstance = L.marker(center, {
              icon: customIcon,
              title: parcel.name,
            }).on("click", () => {
              toggledPolygonsRef.current.add(parcel.id);
              layerGroup.removeLayer(markerInstance);
              layerGroup.addLayer(polygon);
              map.flyToBounds(polygon.getBounds(), { duration: 0.7 });

              if (
                currentParcelRef.current &&
                currentParcelRef.current.id === parcel.id
              ) {
                onClick?.(currentParcelRef.current, polygon, true);
              } else {
                onClick?.(parcel, polygon, false);
              }
            });

            markerMapRef.current.set(parcel.id, markerInstance);
            layerGroup.addLayer(markerInstance);
          } else if (marker && !layerGroup.hasLayer(marker)) {
            layerGroup.addLayer(marker);
          }
        }
      });
    };

    const handleMapClick = (e: L.LeafletMouseEvent) => {
      if (editingPolygonRef.current) {
        const clickedInside = editingPolygonRef.current
          .getBounds()
          .contains(e.latlng);
        if (!clickedInside) {
          editingPolygonRef.current.editing.disable();
          editingPolygonRef.current = null;
        }
      }
    };

    map.on("click", handleMapClick);
    map.on("zoomend", updateVisibility);
    updateVisibility();

    return () => {
      map.off("click", handleMapClick);
      map.off("zoomend", updateVisibility);
      layerGroup.clearLayers();
    };
  }, [map, data, onClick, isReadOnly]);

  useEffect(() => {
    const syncPolygon = (
      parcel: ParcelLocationType,
      targetMap: Map<number, L.Polygon>,
      type: 1 | 2
    ) => {
      const existing = targetMap.get(parcel.id);
      const style = {
        color: parcel.strokeColor,
        weight: parcel.strokeWidth,
        fillColor: parcel.fillColor,
        opacity: 1,
        fillOpacity: parcel.opacity ?? 0.4,
      };

      if (existing) {
        const latLngs = existing.getLatLngs()[0] as L.LatLng[];
        const currentCoords = latLngs.map((c) => [c.lat, c.lng]);

        const coordsChanged =
          JSON.stringify(currentCoords) !== JSON.stringify(parcel.coordinates);
        const styleChanged =
          existing.options.color !== style.color ||
          existing.options.fillColor !== style.fillColor ||
          existing.options.weight !== style.weight ||
          existing.options.fillOpacity !== style.fillOpacity ||
          existing.options.opacity !== style.opacity;

        if (coordsChanged || styleChanged) {
          updatePolygonInstance(parcel);
        }

        return;
      }

      const isHighlighted = toggledPolygonsRef.current.has(parcel.id);
      const polygon = L.polygon(parcel.coordinates, {
        ...style,
        pane: isHighlighted ? "highlightedPolygonPane" : "defaultPolygonPane",
      });

      polygon.idBdd = parcel.id;

      polygon.on("click", (e) => {
        e.originalEvent.stopPropagation();

        if (typeof onClick === "function") {
          if (
            currentParcelRef.current &&
            currentParcelRef.current.id === parcel.id
          ) {
            onClick(currentParcelRef.current, polygon, true);
          } else {
            onClick(parcel, polygon, false);
          }
        }

        map?.flyToBounds(polygon.getBounds(), { duration: 0.7 });
      });

      targetMap.set(parcel.id, polygon);
    };

    if (data) {
      const allParcels = data.flatMap((f) => f.parcels || []);
      const allLocations = data.flatMap((f) => f.locations || []);
      allLocations.forEach((p) => syncPolygon(p, polygonMapRef.current, 2));
      allParcels.forEach((p) => syncPolygon(p, parcelMapRef.current, 1));
    }
  }, [data]);

  return {
    polygonMapRef,
    markerMapRef,
  };
};
